import type { Address } from "viem"
import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { useConfig } from "wagmi"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenTrue } from "@/components/conditionals"
import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MessageBox } from "@/domains/common/components/message-box"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { WalletIcon } from "@/domains/icons"
import { stakeLiquidityActionInteraction } from "@/domains/interactions/stake-liquidity/stake-liquidity-interaction"
import { trimEthAddress } from "@/domains/library"
import { LiquidityPageLayout } from "@/domains/liquidity-page/components/liquidity-page-layout"
import { LiquidityPageTabs } from "@/domains/liquidity-page/components/liquidity-page-tabs"
import { MinimalPoolInputCard } from "@/domains/minimal-pool/components/minimum-pool-input-card"
import { ClassicLiquidityPagesComplexBreadcrumbApplication } from "@/domains/navigation/components/complex-breadcrumb-applications/classic-liquidity-pages-complex-breadcrumb-application"
import { ClassicLiquiditySubItem } from "@/domains/navigation/subitems/classic-liquidity-subitem"
import { TokenInputBalance } from "@/domains/tokens/components/token-input-balance"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useGovernanceToken } from "@/hooks/use-governance-token"
import { useMinimalPool } from "@/hooks/use-minimal-pool"
import useEventEmitter from "@/hooks/useEventEmitter"

const BREADCRUMB_ITEMS = [new ClassicLiquiditySubItem(), ClassicLiquidityPagesComplexBreadcrumbApplication]

const StakePage = observer(() => {
  const { address: poolAddressParam } = useParams<{ address: string }>()
  const config = useConfig()
  const chainConfig = useChainConfig()
  const emitter = useEventEmitter()
  const governanceToken = useGovernanceToken()
  const scaleApp = useScaleApplication()

  const poolAddress = poolAddressParam ? (poolAddressParam as Address) : undefined
  const minimalPool = useMinimalPool(poolAddress)

  // Hook up the orchestrator to run the loadUserManageMinimalPoolInfo query
  useEffect(() => {
    if (!minimalPool || !scaleApp) return
    scaleApp.MinimalPools.Orchestrator.startUserManagePoolSync(minimalPool, 30_000)
    return () => {
      scaleApp.MinimalPools.Orchestrator.stopUserManagePoolSync()
    }
  }, [minimalPool, scaleApp])

  const [amount, setAmount] = useState<string>("0")
  const amountWei = minimalPool?.parseUnits(amount) ?? 0n

  const hasGauge = minimalPool?.hasGauge ?? false
  const canInteract = amountWei > 0n && amountWei < (minimalPool?.userBalance ?? 0n) && !!minimalPool

  const stakeAction = async () => {
    if (!scaleApp.Wallet.Connection.isConnected) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }
    if (!scaleApp.Wallet.Connection.connectedAddress) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }

    if (!minimalPool || !chainConfig) {
      console.error("Missing required dependencies")
      return
    }

    const chainId = chainConfig.chainId
    const _result = await stakeLiquidityActionInteraction({
      connectedAddress: scaleApp?.Wallet.Connection.connectedAddress?.toViemAddress(),
      chainId,
      config,
      emitter,
      amountWei,
      minimalPool,
      approvalMode: scaleApp?.Settings.approvalMode,
      contractRiskLevel: "immutable",
      onSuccess: () => {
        setAmount("0")
        scaleApp.MinimalPools.Orchestrator.userManagePoolQueryInstance?.forceExecute()
      },
    })
  }

  const title = minimalPool ? `Stake ${minimalPool.displayName} Liquidity` : "Stake Liquidity"
  const subTitle = minimalPool
    ? `in ${minimalPool.displayName} to earn ${governanceToken.symbol}`
    : `in the pool to earn ${governanceToken.symbol}`

  return (
    <TooltipProvider>
      <PageContainer>
        <MetaData title={title} />
        <PageTitle title="Stake Liquidity" subTitle={subTitle} items={BREADCRUMB_ITEMS} />
        <LiquidityPageLayout minimalPool={minimalPool} page="stake">
          <LiquidityPageTabs />
          <Card.Container className="border-p400-light bg-p400 min-w-[320px]flex-col flex w-full gap-6 rounded-lg border p-4 lg:p-6">
            <ShowWhenTrue when={!!minimalPool}>
              <Card.Header>
                <div className="flex w-full flex-col">
                  <Card.HeaderDescription>
                    Users staked in the {minimalPool?.displayName} are currently earning{" "}
                    <span className="text-t200">{minimalPool?.displayApr}</span> APR
                  </Card.HeaderDescription>
                </div>
              </Card.Header>

              {minimalPool !== undefined ? (
                <MinimalPoolInputCard
                  amount={amount}
                  balance={minimalPool?.onChainUserBalance ?? 0n}
                  minimalPool={minimalPool}
                  onAmountChange={setAmount}
                  balanceSlot={
                    <TokenInputBalance
                      token={minimalPool}
                      label={<WalletIcon size={14} className="text-t200" />}
                      className="text-t200"
                    />
                  }
                />
              ) : null}

              <div className="flex w-full justify-end">
                <ShowWhenTrue when={hasGauge}>
                  <Button disabled={!canInteract} variant={"primary"} onClick={() => stakeAction()} size="fit">
                    Stake
                  </Button>
                </ShowWhenTrue>
                <ShowWhenTrue when={!hasGauge}>
                  <Button disabled variant={"primary"} onClick={() => stakeAction()} size="fit">
                    No Gauge Available
                  </Button>
                </ShowWhenTrue>
              </div>
            </ShowWhenTrue>
            <ShowWhenTrue when={minimalPool === null}>
              <PairNotFound pairAddress={poolAddress} />
            </ShowWhenTrue>
            <ShowWhenTrue when={minimalPool === undefined}>
              <SearchingForPair />
            </ShowWhenTrue>
          </Card.Container>
        </LiquidityPageLayout>
      </PageContainer>
    </TooltipProvider>
  )
})

export default StakePage

export const SearchingForPair = () => {
  return <div>Searching for pair...</div>
}

const PairNotFound = ({ pairAddress }: { pairAddress: Address | undefined }) => {
  return (
    <MessageBox variant={"error"}>
      <p>
        Couldn't find pair with address {trimEthAddress(pairAddress ?? "")}, click{" "}
        <Link to={"/liquidity"} className="underline">
          here
        </Link>{" "}
        to go back to Liquidity.
      </p>
    </MessageBox>
  )
}
